/**
 * «Tu campaña» (29/10/2026): cómo va la campaña, en todos los pasos del asistente. En la computadora es una columna a la
 * derecha; en el celular, una línea en la barra de abajo («612 personas · hasta USD 18») que la abre encima
 * (`ResumenEnLaBarra` y `TuCampanaEnElCelular`). Los botones miden 44 px de alto.
 *
 * Lleva el nombre (se cambia acá), de qué serie es si es una repetición, a quién (en «A quién», lo elegido que todavía no
 * se guardó, con «Se guarda con «Siguiente»»), el mensaje, cuándo sale, el costo máximo, lo que la frena y los avisos de
 * la cuenta. Cada renglón lleva a su paso, si ya se puede ir. No pide nada: lo arma `useTuCampana` con lo que ya
 * calcularon los pasos.
 */
import { useEffect, useId, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, ChevronUp, Loader2, Repeat, X } from "lucide-react";
import type { PasoAsistente } from "../../logic/chequeo";
import { NOMBRE_DEL_PASO, type RenglonDelPanel } from "../../logic/tuCampana";
import type { DatosTuCampana } from "../../hooks/asistente/useTuCampana";
import { ChipEstadoPlantilla } from "../ChipEstadoPlantilla";

interface Props {
  datos: DatosTuCampana;
  /** sin borrador todavía, el nombre no se cambia: nace con el de `datos` */
  onGuardarNombre?: (n: string) => Promise<string>;
  paso: PasoAsistente;
  /** si ya se puede ir a ese paso (lo guardado lo permite) */
  alcanzable: (p: PasoAsistente) => boolean;
  onIrA: (p: PasoAsistente) => void;
}

export function TuCampana({ datos, onGuardarNombre, paso, alcanzable, onIrA }: Props) {
  const renglon = (p: PasoAsistente, titulo: string, contenido: ReactNode, testid: string) => {
    const cuerpo = (
      <>
        <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-500">{titulo}</span>
        <span className="block text-sm text-slate-800">{contenido}</span>
      </>
    );
    return (
      <li data-testid={testid}>
        {/* lleva a su paso si ya se puede ir y no es el de ahora */}
        {p !== paso && alcanzable(p) ? (
          <button type="button" onClick={() => onIrA(p)}
            className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50">
            <span className="min-w-0">{cuerpo}</span>
            <ChevronRight size={15} className="flex-shrink-0 text-slate-400" aria-hidden="true" />
          </button>
        ) : (
          <div className="px-2 py-1.5">{cuerpo}</div>
        )}
      </li>
    );
  };

  return (
    <section aria-label="Tu campaña" className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm space-y-3">
      <p className="px-2 text-xs font-bold uppercase tracking-[0.1em] text-violet-700">Tu campaña</p>
      <NombreDeLaCampana nombre={datos.nombre} onGuardar={onGuardarNombre} />
      {datos.repeticion && (
        <Link to={datos.repeticion.ruta} data-testid="tu-campana-repeticion"
          className="flex min-h-[44px] items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-violet-700 hover:bg-violet-50">
          <Repeat size={14} className="flex-shrink-0" aria-hidden="true" /> {datos.repeticion.texto}
        </Link>
      )}
      <ul className="space-y-0.5">
        {renglon("audiencia", "A quién", (
          <>
            {datos.audiencia.texto}
            {datos.audiencia.cuantos && <span className="block text-slate-500">{datos.audiencia.cuantos}</span>}
            {/* lo elegido en «A quién» que todavía no se guardó */}
            {datos.audiencia.sinGuardar && <span className="block text-xs font-semibold text-violet-700" data-testid="tu-campana-sin-guardar">Se guarda con «Siguiente»</span>}
          </>
        ), "tu-campana-audiencia")}
        {renglon("mensaje", "El mensaje", datos.mensaje ? (
          <>
            <span className="flex flex-wrap items-center gap-1.5">«{datos.mensaje.nombre}» {datos.mensaje.estado && <ChipEstadoPlantilla estado={datos.mensaje.estado} />}</span>
            {datos.mensaje.previa && <span className="mt-1 block line-clamp-3 rounded-lg bg-emerald-50 px-2 py-1 text-xs text-slate-700">{datos.mensaje.previa}</span>}
          </>
        ) : "Todavía no elegiste la plantilla.", "tu-campana-mensaje")}
        {renglon("cuando", "Cuándo sale", datos.cuando, "tu-campana-cuando")}
        {renglon("revisar", "Costo máximo", datos.costo ? `${datos.costo} del saldo` : "Se calcula al revisar.", "tu-campana-costo")}
      </ul>
      {datos.frenos.length > 0 && <Renglones titulo="Lo que la frena" renglones={datos.frenos} paso={paso} alcanzable={alcanzable} onIrA={onIrA} testid="tu-campana-frenos" />}
      {datos.avisos.length > 0 && <Renglones titulo="Tu cuenta" renglones={datos.avisos} paso={paso} alcanzable={alcanzable} onIrA={onIrA} testid="tu-campana-avisos" />}
    </section>
  );
}

/** El nombre, y cambiarlo: se guarda al confirmar («Guardar» o Enter), no en cada tecla. */
function NombreDeLaCampana({ nombre, onGuardar }: { nombre: string; onGuardar?: (n: string) => Promise<string> }) {
  const idCampo = useId();
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(nombre);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    if (!onGuardar || guardando) return;
    setGuardando(true);
    const err = await onGuardar(valor);
    setGuardando(false);
    setError(err);
    if (!err) setEditando(false);
  };
  if (!editando) {
    return (
      <div className="flex items-center justify-between gap-2 px-2">
        <p className="min-w-0 break-words text-base font-semibold text-slate-900" data-testid="tu-campana-nombre">{nombre}</p>
        {onGuardar && (
          <button type="button" onClick={() => { setValor(nombre); setError(""); setEditando(true); }} aria-label="Cambiar el nombre"
            className="h-11 flex-shrink-0 rounded-lg px-3 text-sm font-semibold text-violet-700 hover:bg-violet-50">
            Cambiar
          </button>
        )}
      </div>
    );
  }
  return (
    <form onSubmit={(e) => void guardar(e)} className="space-y-2 px-2">
      <label htmlFor={idCampo} className="text-xs font-semibold text-slate-600">Nombre de la campaña</label>
      <input id={idCampo} value={valor} maxLength={120} autoFocus onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") setEditando(false); }}
        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
      {error && <p className="text-xs text-red-700" role="alert">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={guardando}
          className="inline-flex h-11 items-center gap-1.5 rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
          {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
        </button>
        <button type="button" onClick={() => setEditando(false)} disabled={guardando}
          className="h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          Cancelar
        </button>
      </div>
    </form>
  );
}

/** «Lo que la frena» y los avisos de la cuenta: cada uno con el paso que lo arregla (si no es el de ahora), o la pantalla. */
function Renglones({ titulo, renglones, paso, alcanzable, onIrA, testid }: {
  titulo: string; renglones: RenglonDelPanel[]; paso: PasoAsistente; alcanzable: (p: PasoAsistente) => boolean; onIrA: (p: PasoAsistente) => void; testid: string;
}) {
  return (
    <div className="rounded-xl bg-amber-50 px-3 py-2" data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-amber-900">{titulo}</p>
      <ul className="mt-1 space-y-1">
        {renglones.map((r) => (
          <li key={r.texto} className="flex flex-wrap items-center justify-between gap-x-2 text-sm text-amber-900">
            <span className="flex min-w-0 items-start gap-1.5 py-1"><AlertTriangle size={13} className="mt-0.5 flex-shrink-0" aria-hidden="true" />{r.texto}</span>
            {r.paso && r.paso !== paso && alcanzable(r.paso) ? (
              <button type="button" onClick={() => onIrA(r.paso as PasoAsistente)} className="h-11 font-semibold underline">Ir a «{NOMBRE_DEL_PASO[r.paso]}»</button>
            ) : r.ruta ? (
              <Link to={r.ruta} className="inline-flex h-11 items-center font-semibold underline">{r.label || "Ver"}</Link>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** La línea de «Tu campaña» en la barra de abajo del celular: la abre encima. */
export function ResumenEnLaBarra({ corto, abierto, onAbrir }: { corto: string; abierto: boolean; onAbrir: () => void }) {
  return (
    <button type="button" onClick={onAbrir} aria-expanded={abierto} aria-haspopup="dialog"
      className="mb-2 flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 text-left text-sm lg:hidden">
      <span className="min-w-0 truncate"><span className="font-semibold text-slate-900">Tu campaña</span>{corto && <span className="text-slate-600"> · {corto}</span>}</span>
      <ChevronUp size={16} className="flex-shrink-0 text-slate-500" aria-hidden="true" />
    </button>
  );
}

/** «Tu campaña» abierta en el celular: encima de la pantalla, desde abajo. Se cierra con la ✕, tocando afuera o con Escape. */
export function TuCampanaEnElCelular({ onCerrar, children }: { onCerrar: () => void; children: ReactNode }) {
  useEffect(() => {
    const alTocar = (e: KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
    document.addEventListener("keydown", alTocar);
    return () => document.removeEventListener("keydown", alTocar);
  }, [onCerrar]);
  return (
    <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Tu campaña">
      <button type="button" onClick={onCerrar} aria-label="Cerrar «Tu campaña»" tabIndex={-1} className="absolute inset-0 h-full w-full bg-slate-900/30" />
      <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-slate-50 p-3 pb-6 shadow-xl">
        <div className="flex justify-end">
          <button type="button" onClick={onCerrar} aria-label="Cerrar" className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
